import {
  AspectRatioPlaceholder,
  Button,
  Dialog,
  ImagePreloader,
  MediaDialog,
  MediaDialogMedia,
  Spinner,
} from "@rio-cloud/rio-uikit";
import { CheckpointStep } from "@types";
import { formatTime, formatTimeFromMinutes } from "@utils";
import { useCallback, useState } from "react";

interface Props {
  open: boolean;
  onClose?: () => void;
  stage?: CheckpointStep;
  totalStages: number;
}

const fallback = (
  <AspectRatioPlaceholder width={64} height={64}>
    <div className="text-size-h3 display-grid place-items-center inset-0">
      <span className="rioglyph rioglyph-missing text-size-60 text-color-gray" />
    </div>
  </AspectRatioPlaceholder>
);

const Image = ({ src, onClick }: { src: string; onClick: () => void }) => {
  return (
    <ImagePreloader src={src}>
      {({ status, image }) => {
        switch (status) {
          case "FAILED":
            return fallback;

          case "LOADED":
            return (
              <div className="cursor-pointer width-150 height-100 rounded overflow-hidden" onClick={onClick}>
                <img src={image.src} className="img-responsive ani-fade-in" alt="Awesome stuff." />
              </div>
            );

          default:
            return (
              <AspectRatioPlaceholder width={64} height={64} className="width-60 height-60">
                <Spinner isInverse={false} />
              </AspectRatioPlaceholder>
            );
        }
      }}
    </ImagePreloader>
  );
};

const DialogCarousel = ({ open, onClose, images }: { open: boolean; onClose: () => void; images: string[] }) => {
  const mediaImages = images
    .filter((src) => src)
    .map((src, index) => ({
      src,
      type: MediaDialog.MEDIA_TYPE_IMAGE,
      title: `Foto ${index + 1}`,
    }));

  if (mediaImages.length === 0) return null;

  return (
    <MediaDialog
      show={open}
      onClose={onClose}
      showCloseButton
      previousButtonText="Anterior"
      useOverflow={false}
      nextButtonText="Próximo"
      bsSize="fullscreen"
      media={mediaImages as MediaDialogMedia[]}
    />
  );
};

const Checkpoint = ({ open, onClose, stage, totalStages }: Props) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null);

  const getFieldUi = (title: string, estimatedValue: string, actualValue: string) => {
    return (
      <div className="width-100pct width-350-md">
        <div>
          <label>
            {title || ""} {estimatedValue ? `(Estimado ${estimatedValue})` : ""}
          </label>
          <input type="text" className="form-control" id="inputEmail" placeholder="" value={actualValue} disabled />
        </div>
      </div>
    );
  };

  const normalizeBase64 = useCallback((base64: string): string[] => {
    const parts = base64.split("data");
    const normalizedImages: string[] = [];

    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      if (part) {
        const normalized = part.endsWith(",") ? part.slice(0, -1) : part;
        normalizedImages.push(`data${normalized}`);
      }
    }

    return normalizedImages;
  }, []);

  return (
    <Dialog
      show={open}
      title={<span className="text-bold text-size-18">{`Etapa ${stage?.index}/${totalStages} - ${stage?.title}`}</span>}
      onClose={onClose}
      bodyClassName="padding-25"
      footer={
        <Button className="width-100" onClick={onClose}>
          Voltar
        </Button>
      }
      body={
        <div className="display-flex flex-column gap-25">
          <div className="display-flex">
            <div>
              <span>Tempo Estimado: </span>
              <span>
                <span className="text-normal text-size-14 rioglyph rioglyph-time text-color-gray" />
                <span className="text-normal text-size-14 margin-left-5">
                  {formatTimeFromMinutes(stage?.estimatedTimeMinutes)}
                </span>
              </span>
            </div>
            <div className="margin-x-20">
              <span>|</span>
            </div>
            <div>
              <span>Tempo Realizado: </span>
              <span>
                <span className="text-normal text-size-14 rioglyph rioglyph-time text-color-gray" />
                <span className="text-normal text-size-14 margin-left-5">{formatTime(stage?.realizedTimeMinutes)}</span>
              </span>
            </div>
          </div>

          <div className="">
            <form>
              <fieldset className="display-flex justify-content-between flex-row flex-wrap">
                {stage?.fields?.map((step) => {
                  const type = step?.field?.type;
                  const title = step?.field?.title;
                  const estimatedValue = step?.estimatedValue || "";
                  const actualValue = step?.actualValue;

                  if (type === "IMAGE") return "";

                  if (step?.field?.name === "OBSERVATION") return "";

                  if (type === "BOOLEAN") return getFieldUi(title, estimatedValue, actualValue ? "Sim" : "Não");

                  return getFieldUi(title, estimatedValue, actualValue || "");
                })}
              </fieldset>
            </form>
          </div>

          {stage?.fields?.map((step) => {
            if (step?.field?.name !== "OBSERVATION") return "";

            return (
              <div key={step.id}>
                <form>
                  <fieldset>
                    <div className="form-group padding-0">
                      <label form="textArea">{step?.field?.title}</label>
                      <textarea
                        disabled
                        className="form-control"
                        rows={3}
                        id="textArea"
                        value={step?.actualValue?.toString() || ""}
                      />
                    </div>
                  </fieldset>
                </form>
              </div>
            );
          })}

          {stage?.fields?.map((step) => {
            if (step?.field?.type !== "IMAGE") return "";

            const images = step?.actualValue ? normalizeBase64(step.actualValue) : [];

            return (
              <div key={step.id}>
                <span className="text-color-dark text-normal text-size-12">{step?.field?.title}</span>
                <div className="display-flex gap-10 margin-top-4 flex-wrap">
                  {images.map((image, index) => (
                    <div key={index} className="min-width-100">
                      <Image src={image} onClick={() => setSelectedPhotoIndex(index)} />
                    </div>
                  ))}
                </div>

                <DialogCarousel
                  open={selectedPhotoIndex !== null}
                  images={images}
                  onClose={() => setSelectedPhotoIndex(null)}
                />
              </div>
            );
          })}
        </div>
      }
      className=""
      showCloseButton
      bsSize="md"
    />
  );
};

export default Checkpoint;
